import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/dist/query/react';
import { logout, tokenReceived } from '../features/auth/authSlice';
import { refrescarSesion } from './refrescarSesion';
import { RootState } from '../redux/store';
import { settings } from '../settings';
import type { BaseQueryFn, FetchArgs, FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { presentacionesApi } from './presentacionesApi';

const BASE_URL = 'cancerigenos';
export interface NewCarcinogenicReport {
    idEstablecimiento: number;
    establecimiento: {
        propiosAdministrativos: number;
        eventualesAdministrativos: number;
        propiosProduccion: number;
        eventualesProduccion: number;
        permiteFumarLugarCerrado: boolean;
        permiteFumar: boolean;
        observacion: string;
    };
    proveedoresCheck: boolean;
    radiologicosCheck: boolean;
}

export interface ForcedWrongNewCarcinogenicReport {
    idEstablecimiento: number;
    establecimiento: {
        propiosAdministrativos: null;
        eventualesAdministrativos: null;
        propiosProduccion: null;
        eventualesProduccion: null;
        permiteFumarLugarCerrado: null;
        permiteFumar: null;
        observacion: null;
    };
    proveedoresCheck: boolean;
    radiologicosCheck: boolean;
}

export interface EditSubstanceBody {
    nombreComercial: string;
    usos: number[];
    modosEmpleo: number[];
    informaRiesgo: boolean;
    capacitaRiesgo: boolean;
    elementosProteccion: number[];
    medidasPrevencion: number[];
    estudiosRemplazo: string;
    licenciaEspecial: boolean;
    cantidadAnual?: number | null;
    idUnidadMedida?: number | null;
}

interface SubstancesParams {
    idEstablecimiento: number;
}

export interface NewSubstanceStudyBody {
    idTipo: number;
    idTipoFrecuencia: number;
    detalle: string;
    cantidad: number;
}

export interface NewProviderBody {
    nombreQuimico: string;
    cuit: string;
    tipo: {};
}

const baseQuery = fetchBaseQuery({
    baseUrl: `${settings.url.host}${settings.url.api}`,
    credentials: 'include',
    prepareHeaders: (headers, { getState }) => {
        const accessToken = (getState() as RootState).auth.accessToken;
        if (accessToken) {
            headers.set('authorization', `Bearer ${accessToken}`);
        }
        return headers;
    }
});
export interface NewResponsibleBody {
    nombre: string;
    apellido: string;
    cuit: string;
    titulo: string;
    matricula: string;
    entidadOtorgante: string;
    idCargo: number;
    idTipoContratacion: number;
    idRepresentacion: number;
    idTipoProfesional: number;
    cantidadHoras: number;
}
export interface NewContractorBody {
    cuit: string;
    idActividad: number;
    tipoEmpresa: { id: number; codigo: string; descripcion: string };
    cantidadTrabajadores: number;
}

const baseQueryWithReauth: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> = async (
    args,
    api,
    extraOptions
) => {
    let result: any = await baseQuery(args, api, extraOptions);

    if (result.error && result.error.status === 401) {
        const refreshResult = await refrescarSesion(baseQuery, api, extraOptions);
        if (refreshResult?.data) {
            api.dispatch(tokenReceived(refreshResult.data));
            result = await baseQuery(args, api, extraOptions);
        } else {
            api.dispatch(logout());
        }
    }
    return result;
};

export const carcinogenicApi = createApi({
    reducerPath: 'carcinogenic',
    baseQuery: baseQueryWithReauth,
    tagTypes: [
        'CarcinogenicParametricData',
        'Substances',
        'CarcinogenicReport',
        'CarcinogenicReportSRT',
        'CarcinogenicReportWorkers',
        'CarcinogenicReportWorkerDetail',
        'Presentaciones'
    ],
    endpoints: (builder) => ({
        createCarcinogenicReport: builder.mutation({
            query: (body: NewCarcinogenicReport | ForcedWrongNewCarcinogenicReport) => ({
                url: `${BASE_URL}`,
                body: body,
                method: 'POST'
            })
        }),
        editCarcinogenicReportEstablishment: builder.mutation({
            query: ({
                reportId,
                body,
                isBorrador
            }: {
                reportId: number;
                body: NewCarcinogenicReport;
                isBorrador?: boolean;
            }) => {
                const { idEstablecimiento, ...rest } = body;
                return {
                    url: `${BASE_URL}/${reportId}`,
                    body: { ...rest, esBorrador: isBorrador === false ? false : true },
                    method: 'PATCH'
                };
            },
            invalidatesTags: (_, error) => (error ? [] : ['CarcinogenicReport'])
        }),
        getCarcinogenicParametricData: builder.query({
            query: (params) => ({
                url: `${BASE_URL}/datos-parametricos`,
                params
            }),
            providesTags: ['CarcinogenicParametricData']
        }),
        getCarcinogenicReport: builder.query({
            query: (reportId: number | null) => {
                return {
                    url: `${BASE_URL}/${reportId}`
                };
            },
            providesTags: ['CarcinogenicReport']
        }),
        getSubstances: builder.query({
            query: (params: SubstancesParams) => ({
                url: `${BASE_URL}/sustancias`,
                params
            }),
            providesTags: ['Substances']
        }),
        editCarcinogenicReport: builder.mutation({
            query: ({ body, reportId }: { body: NewCarcinogenicReport; reportId: number }) => ({
                url: `${BASE_URL}/sustancias/${reportId}`,
                method: 'PATCH',
                body
            }),
            invalidatesTags: (_, error) => (error ? [] : ['CarcinogenicReport'])
        }),
        editCarcinogenicSubstance: builder.mutation({
            query: ({
                body,
                reportId,
                substanceId
            }: {
                body: EditSubstanceBody;
                reportId: number;
                substanceId: number;
            }) => ({
                url: `${BASE_URL}/${reportId}/sustancias/${substanceId}`,
                method: 'PATCH',
                body
            }),
            invalidatesTags: (_, error) => (error ? [] : ['CarcinogenicReport'])
        }),
        createCarcinogenicSubstanceReport: builder.mutation({
            query: ({
                body,
                reportId,
                substanceId
            }: {
                body: NewCarcinogenicReport;
                reportId: number;
                substanceId: number;
            }) => ({
                url: `${BASE_URL}/sustancias/${reportId}/sustancias/${substanceId}/estudios`,
                body
            }),
            invalidatesTags: (_, error) => (error ? [] : ['CarcinogenicReport'])
        }),
        deleteCarcinogenicSubstanceStudy: builder.mutation({
            query: ({
                reportId,
                substanceId,
                studyId
            }: {
                reportId: number;
                substanceId: number;
                studyId: number;
            }) => ({
                url: `${BASE_URL}/${reportId}/sustancias/${substanceId}/estudios/${studyId}`,
                method: 'DELETE'
            }),
            invalidatesTags: (_, error) => (error ? [] : ['CarcinogenicReport'])
        }),
        editCarcinogenicSubstanceStudy: builder.mutation({
            query: ({
                reportId,
                substanceId,
                studyId
            }: {
                reportId: number;
                substanceId: number;
                studyId: number;
            }) => ({
                url: `${BASE_URL}/sustancias/${reportId}/sustancias/${substanceId}/estudios/${studyId}`,
                method: 'PATCH'
            }),
            invalidatesTags: (_, error) => (error ? [] : ['CarcinogenicReport'])
        }),
        createCarcinogenicSubstanceStudy: builder.mutation({
            query: ({
                reportId,
                substanceId,
                body
            }: {
                reportId: number;
                substanceId: number;
                body: NewSubstanceStudyBody;
            }) => ({
                url: `${BASE_URL}/${reportId}/sustancias/${substanceId}/estudios`,
                body: body,
                method: 'POST'
            }),
            invalidatesTags: (_, error) => (error ? [] : ['CarcinogenicReport'])
        }),
        createCarcinogenicProvider: builder.mutation({
            query: ({
                reportId,
                substanceId,
                body
            }: {
                reportId: number;
                substanceId: number;
                body: NewProviderBody;
            }) => ({
                url: `${BASE_URL}/${reportId}/sustancias/${substanceId}/proveedores`,
                body: body,
                method: 'POST'
            }),
            invalidatesTags: (_, error) => (error ? [] : ['CarcinogenicReport'])
        }),
        editCarcinogenicProvider: builder.mutation({
            query: ({
                reportId,
                substanceId,
                providerId,
                body
            }: {
                reportId: number;
                substanceId: number;
                providerId: number;
                body: NewProviderBody;
            }) => ({
                url: `${BASE_URL}/${reportId}/sustancias/${substanceId}/proveedores/${providerId}`,
                body: body,
                method: 'PATCH'
            }),
            invalidatesTags: (_, error) => (error ? [] : ['CarcinogenicReport'])
        }),
        deleteCarcinogenicProvider: builder.mutation({
            query: ({
                reportId,
                substanceId,
                providerId
            }: {
                reportId: number;
                substanceId: number;
                providerId: number;
            }) => ({
                url: `${BASE_URL}/${reportId}/sustancias/${substanceId}/proveedores/${providerId}`,
                method: 'DELETE'
            }),
            invalidatesTags: (_, error) => (error ? [] : ['CarcinogenicReport'])
        }),
        createRadiologicalEquipment: builder.mutation({
            query: ({
                reportId,
                substanceId,
                body
            }: {
                reportId: number;
                substanceId: number;
                body: any;
            }) => ({
                url: `${BASE_URL}/${reportId}/sustancias/${substanceId}/equipos`,
                body: body,
                method: 'POST'
            }),
            invalidatesTags: (_, error) => (error ? [] : ['CarcinogenicReport'])
        }),
        editCarcinogenicEquipment: builder.mutation({
            query: ({
                reportId,
                substanceId,
                equipmentId,
                body
            }: {
                reportId: number;
                substanceId: number;
                equipmentId: number;
                body: any;
            }) => ({
                url: `${BASE_URL}/${reportId}/sustancias/${substanceId}/equipos/${equipmentId}`,
                body: body,
                method: 'PATCH'
            }),
            invalidatesTags: (_, error) => (error ? [] : ['CarcinogenicReport'])
        }),
        deleteCarcinogenicEquipment: builder.mutation({
            query: ({
                reportId,
                substanceId,
                equipmentId
            }: {
                reportId: number;
                substanceId: number;
                equipmentId: number;
            }) => ({
                url: `${BASE_URL}/${reportId}/sustancias/${substanceId}/equipos/${equipmentId}`,
                method: 'DELETE'
            }),
            invalidatesTags: (_, error) => (error ? [] : ['CarcinogenicReport'])
        }),
        deleteCarcinogenicEquipmentResponsible: builder.mutation({
            query: ({
                reportId,
                substanceId,
                equipmentId,
                responsibleId
            }: {
                reportId: number;
                substanceId: number;
                equipmentId: number;
                responsibleId: number;
            }) => ({
                url: `${BASE_URL}/${reportId}/sustancias/${substanceId}/equipos/${equipmentId}/responsables/${responsibleId}`,
                method: 'DELETE'
            }),
            invalidatesTags: (_, error) => (error ? [] : ['CarcinogenicReport'])
        }),
        getCarcinogenicReportWorkers: builder.query({
            query: ({ reportId, filters }) => {
                return {
                    url: `${BASE_URL}/${reportId}/trabajadores`,
                    params: filters
                };
            },
            providesTags: ['CarcinogenicReportWorkers']
        }),
        getCarcinogenicReportWorkerDetail: builder.query({
            query: ({ reportId, wokerId }) => {
                return {
                    url: `${BASE_URL}/${reportId}/trabajadores/${wokerId}`
                };
            },
            providesTags: ['CarcinogenicReportWorkers']
        }),
        editCarcinogenicReportWorkerDetail: builder.mutation({
            query: ({ reportId, workerId, body }) => {
                return {
                    url: `${BASE_URL}/${reportId}/trabajadores/${workerId}`,
                    body: body,
                    method: 'PATCH'
                };
            },
            invalidatesTags: [
                'CarcinogenicReportWorkerDetail',
                'CarcinogenicReportWorkers',
                'CarcinogenicReport'
            ]
        }),
        addCarcinogenicReportContractor: builder.mutation({
            query: ({ reportId, body }: { reportId: number; body: NewContractorBody }) => ({
                url: `${BASE_URL}/${reportId}/contratistas`,
                body: { ...body, tipoEmpresa: body?.tipoEmpresa?.id },
                method: 'POST'
            }),
            invalidatesTags: (_, error) => (error ? [] : ['CarcinogenicReport'])
        }),
        editCarcinogenicReportContractor: builder.mutation({
            query: ({
                reportId,
                contractorId,
                body
            }: {
                reportId: number;
                contractorId: number;
                body: NewContractorBody;
            }) => ({
                url: `${BASE_URL}/${reportId}/contratistas/${contractorId}`,
                body: body,
                method: 'PATCH'
            }),
            invalidatesTags: (_, error) => (error ? [] : ['CarcinogenicReport'])
        }),
        deleteCarcinogenicContractor: builder.mutation({
            query: ({ reportId, contractorId }: { reportId: number; contractorId: number }) => ({
                url: `${BASE_URL}/${reportId}/contratistas/${contractorId}`,
                method: 'DELETE'
            }),
            invalidatesTags: (_, error) => (error ? [] : ['CarcinogenicReport'])
        }),
        addCarcinogenicReportResponsible: builder.mutation({
            query: ({ reportId, body }: { reportId: number; body: NewResponsibleBody }) => ({
                url: `${BASE_URL}/${reportId}/responsables`,
                body: body,
                method: 'POST'
            }),
            invalidatesTags: (_, error) => (error ? [] : ['CarcinogenicReport'])
        }),
        editCarcinogenicReportResponsible: builder.mutation({
            query: ({
                reportId,
                responsibleId,
                body
            }: {
                reportId: number;
                responsibleId: number;
                body: NewResponsibleBody;
            }) => ({
                url: `${BASE_URL}/${reportId}/responsables/${responsibleId}`,
                body: body,
                method: 'PATCH'
            }),
            invalidatesTags: (_, error) => (error ? [] : ['CarcinogenicReport'])
        }),
        deleteCarcinogenicReportResponsible: builder.mutation({
            query: ({ reportId, responsibleId }: { reportId: number; responsibleId: number }) => ({
                url: `${BASE_URL}/${reportId}/responsables/${responsibleId}`,
                method: 'DELETE'
            }),
            invalidatesTags: (_, error) => (error ? [] : ['CarcinogenicReport'])
        }),
        vincularCarcinogenicReport: builder.mutation({
            query: (idInforme: number) => ({
                url: `${BASE_URL}/${idInforme}/vincular`,
                method: 'POST'
            }),
            invalidatesTags: (_, error) => (error ? [] : ['CarcinogenicReport'])
        }),
        getCarcinogenicDetailsHistory: builder.query({
            query: (idInforme: any) => ({
                url: `${BASE_URL}/srt/presentaciones/${idInforme}`
            }),
            providesTags: (_, error) => (error ? [] : ['CarcinogenicReportSRT'])
        }),
        // Historial real de pasos (timestamp propio por paso) — alimenta la
        // línea de tiempo del drawer con fechas reales en vez de inferir el
        // estado por comparación de índice contra un único `paso` actual.
        getCarcinogenicSrtHistorial: builder.query({
            query: (idInforme: any) => ({
                url: `${BASE_URL}/srt/presentaciones/${idInforme}/historial`
            }),
            providesTags: (_, error) => (error ? [] : ['CarcinogenicReportSRT'])
        }),
        // Validación pre-envío: qué le falta / qué está mal al informe según
        // las mismas reglas que valida la SRT, para corregirlo ANTES de mandar.
        getCarcinogenicSrtValidaciones: builder.query({
            query: (idInforme: any) => ({
                url: `${BASE_URL}/srt/presentaciones/${idInforme}/validaciones`
            }),
            /**
             * También `CarcinogenicReport`: el panel de la pantalla del cliente
             * tiene que reevaluar el informe cada vez que el usuario guarda algo,
             * y todas las mutations de los módulos invalidan ese tag. Con sólo
             * `CarcinogenicReportSRT` el panel quedaba congelado hasta que
             * alguien vinculara o consolidara desde el backoffice.
             */
            providesTags: (_, error) =>
                error ? [] : ['CarcinogenicReportSRT', 'CarcinogenicReport']
        }),
        /**
         * Validación pre-envío de todo el lote en una sola llamada: el drawer
         * necesita el veredicto de todos los establecimientos del CUIT.
         *
         * Es una `query` y no una `mutation` aunque viaje por POST: no cambia
         * nada del servidor, y como mutation no cacheaba, así que cada apertura
         * del drawer revalidaba lo mismo. Con los mismos tags que la validación
         * por informe, cualquier mutation que pueda cambiar el veredicto
         * (vincular, consolidar, cancelar, modificar, o una edición del informe)
         * ya la invalida.
         */
        getCarcinogenicSrtValidacionesLote: builder.query({
            query: (idInformes: number[]) => ({
                url: `${BASE_URL}/srt/presentaciones/validaciones`,
                method: 'POST',
                body: { idInformes }
            }),
            /*
             * Clave de caché independiente del orden: `[11, 10]` y `[10, 11]`
             * son el mismo pedido, y sin esto abrían dos entradas.
             *
             * El guard de `Array.isArray` no es defensivo de más: con
             * `skip: true` RTK Query pasa `skipToken`, que es un Symbol, y
             * spreadearlo tira "symbol is not iterable".
             */
            serializeQueryArgs: ({ endpointName, queryArgs }) =>
                Array.isArray(queryArgs)
                    ? `${endpointName}(${Array.from(new Set(queryArgs as number[]))
                          .sort((a, b) => a - b)
                          .join(',')})`
                    : `${endpointName}(${String(queryArgs)})`,
            providesTags: (_, error) =>
                error ? [] : ['CarcinogenicReportSRT', 'CarcinogenicReport']
        }),
        sendCarcinogenicSRT: builder.mutation({
            query: (idInforme: number) => ({
                url: `${BASE_URL}/${idInforme}/vincular`,
                method: 'POST'
            }),
            onQueryStarted: async (_arg, { dispatch, queryFulfilled }) => {
                try {
                    await queryFulfilled;
                } catch {
                    // Prevent unhandled promise rejections when the mutation fails.
                } finally {
                    dispatch(presentacionesApi.util.invalidateTags(['Presentaciones']));
                    dispatch(carcinogenicApi.util.invalidateTags(['CarcinogenicReportSRT']));
                }
            }
        }),
        cancelCarcinogenicSRTByContract: builder.mutation({
            query: (cuit: string) => ({
                url: `${BASE_URL}/srt/cuit/${cuit}/cancelar`,
                method: 'DELETE'
            }),
            onQueryStarted: async (_arg, { dispatch, queryFulfilled }) => {
                try {
                    await queryFulfilled;
                } catch {
                    // Prevent unhandled promise rejections when the mutation fails.
                } finally {
                    dispatch(presentacionesApi.util.invalidateTags(['Presentaciones']));
                    dispatch(carcinogenicApi.util.invalidateTags(['CarcinogenicReportSRT']));
                }
            }
        }),
        /**
         * Trae del WS de Novedades de la SRT si el empleador confirmó la
         * presentación consolidada. Consolidar no cierra el circuito: hasta que
         * el empleador confirme (o pasen los 10 días corridos y se confirme
         * automáticamente) no se puede modificar ni iniciar otra presentación
         * para el CUIT.
         *
         * `organizacion` no se manda: el backend la resuelve por el CUIT.
         */
        syncCarcinogenicSRTNews: builder.mutation({
            query: (cuit: string) => ({
                url: `${BASE_URL}/srt/novedades/sincronizar`,
                method: 'POST',
                body: { cuit }
            }),
            onQueryStarted: async (_arg, { dispatch, queryFulfilled }) => {
                try {
                    await queryFulfilled;
                } catch {
                    // Prevent unhandled promise rejections when the mutation fails.
                } finally {
                    dispatch(presentacionesApi.util.invalidateTags(['Presentaciones']));
                    dispatch(carcinogenicApi.util.invalidateTags(['CarcinogenicReportSRT']));
                }
            }
        }),
        consolidateCarcinogenicSRT: builder.mutation({
            query: (cuit: string) => ({
                url: `${BASE_URL}/srt/cuit/${cuit}/consolidar`,
                method: 'POST'
            }),
            onQueryStarted: async (_arg, { dispatch, queryFulfilled }) => {
                try {
                    await queryFulfilled;
                } catch {
                    // Prevent unhandled promise rejections when the mutation fails.
                } finally {
                    dispatch(presentacionesApi.util.invalidateTags(['Presentaciones']));
                    dispatch(carcinogenicApi.util.invalidateTags(['CarcinogenicReportSRT']));
                }
            }
        }),
        // Modificación de una presentación YA consolidada: reenvía a la SRT los
        // datos corregidos (ModificacionDeAnexoI/V/Nomina).
        modifyCarcinogenicSRT: builder.mutation({
            query: (cuit: string) => ({
                url: `${BASE_URL}/srt/cuit/${cuit}/modificar`,
                method: 'POST'
            }),
            onQueryStarted: async (_arg, { dispatch, queryFulfilled }) => {
                try {
                    await queryFulfilled;
                } catch {
                    // Prevent unhandled promise rejections when the mutation fails.
                } finally {
                    dispatch(presentacionesApi.util.invalidateTags(['Presentaciones']));
                    dispatch(carcinogenicApi.util.invalidateTags(['CarcinogenicReportSRT']));
                }
            }
        }),
        getCarcinogenicPendingByCuit: builder.query({
            query: (cuit: string) => ({
                url: `${BASE_URL}/srt/cuit/${cuit}/pendientes`
            })
        }),
        // Sin filtro de período: la grilla lista todo lo presentable del alcance
        // del usuario. El período no es un dato de `presentaciones`, y derivarlo
        // de la fecha de presentación clasificaba mal.
        /**
         * Grilla paginada por CUIT. El backend resuelve paginación y filtros y
         * devuelve `{ data, count, totalCount, skip, take, conteos }`: los
         * conteos de los badges se cuentan allá porque acá sólo hay una página.
         */
        getCuitPresentacionesSvcc: builder.query({
            query: (arg?: {
                idOrganizacion?: number;
                cuit?: string;
                estado?: string;
                /** Un año de 4 dígitos, o `SIN_PERIODO` para las filas sin declarar. */
                anioPeriodo?: string;
                skip?: number;
                take?: number;
            }) => ({
                url: `${BASE_URL}/srt/cuit/presentaciones`,
                params: {
                    ...(arg?.idOrganizacion ? { idOrganizacion: arg.idOrganizacion } : {}),
                    ...(arg?.cuit ? { cuit: arg.cuit } : {}),
                    ...(arg?.estado ? { estado: arg.estado } : {}),
                    ...(arg?.anioPeriodo ? { anioPeriodo: arg.anioPeriodo } : {}),
                    // `skip: 0` es un valor válido, así que se compara contra
                    // undefined y no por truthiness.
                    ...(arg?.skip !== undefined ? { skip: arg.skip } : {}),
                    ...(arg?.take !== undefined ? { take: arg.take } : {})
                }
            }),
            providesTags: ['CarcinogenicReportSRT']
        }),
        getCarcinogenicSRTDebugByCuit: builder.query({
            query: (cuit: string) => ({
                url: `${BASE_URL}/srt/cuit/${cuit}/obtener`
            })
        }),
        verificarSincronizacionSrt: builder.query({
            query: (cuit: string) => ({
                url: `${BASE_URL}/srt/cuit/${cuit}/verificar-sincronizacion`
            })
        }),
        /**
         * Validación pre-envío por CUIT, para que la grilla pueda deshabilitar el
         * envío de un lote que la SRT va a rechazar sin obligar a abrir el drawer
         * de cada fila. Devuelve el resumen (bloqueantes/advertencias) Y el
         * detalle por informe, que es lo que muestra el drawer: el backend ya lo
         * calcula para contar, así que viaja en la misma respuesta y el drawer no
         * necesita pedirlo de nuevo.
         *
         * `query` y no `mutation` por el mismo motivo que el lote por informes:
         * no muta nada y necesita caché e invalidación por tags.
         */
        getValidacionesCuitsSvcc: builder.query({
            query: (cuits: string[]) => ({
                url: `${BASE_URL}/srt/cuit/validaciones`,
                method: 'POST',
                body: { cuits }
            }),
            // Misma razón que en el lote por informes: el orden de los CUIT no
            // define un pedido distinto, y `skipToken` no es un array.
            serializeQueryArgs: ({ endpointName, queryArgs }) =>
                Array.isArray(queryArgs)
                    ? `${endpointName}(${Array.from(new Set(queryArgs as string[]))
                          .sort()
                          .join(',')})`
                    : `${endpointName}(${String(queryArgs)})`,
            providesTags: (_, error) =>
                error ? [] : ['CarcinogenicReportSRT', 'CarcinogenicReport']
        }),
        /**
         * Le avisa al cliente que su presentación fue rechazada, para que
         * corrija lo que la SRT objetó. Encola el evento
         * `SVCC_RECHAZADA_NOTIFICAR_CLIENTE`.
         *
         * El backend exige un registro SRT previo para el CUIT, así que sólo
         * tiene sentido ofrecerlo en filas que ya intentaron presentar.
         */
        notificarClienteSvcc: builder.mutation({
            query: (cuit: string) => ({
                url: `${BASE_URL}/srt/cuit/${cuit}/notificar-cliente`,
                method: 'POST'
            })
        }),
        notificarCorreccionCargada: builder.mutation({
            query: (cuit: string) => ({
                url: `${BASE_URL}/srt/cuit/${cuit}/notificar-correccion`,
                method: 'POST'
            })
        }),
        getEstadoSvccPorInforme: builder.query({
            query: (idInforme: number) => ({
                url: `${BASE_URL}/srt/informe/${idInforme}/estado-svcc-cliente`
            }),
            // También `CarcinogenicReport`: la respuesta trae cuántas observaciones
            // quedan, y eso cambia cada vez que el cliente guarda el informe. Sin
            // este tag el botón "Notificar corrección" seguía cerrado después de
            // corregir, hasta recargar la página.
            providesTags: ['CarcinogenicReportSRT', 'CarcinogenicReport']
        }),
        // Informes SVCC del establecimiento con correcciones pendientes, para el
        // aviso de la página de presentaciones. Mismos tags que el estado por
        // informe: guardar el informe cambia cuántas correcciones quedan.
        getCorreccionesPendientesEstablecimiento: builder.query({
            query: (idEstablecimiento: number) => ({
                url: `${BASE_URL}/srt/establecimiento/${idEstablecimiento}/correcciones-pendientes`
            }),
            providesTags: ['CarcinogenicReportSRT', 'CarcinogenicReport']
        }),
        vincularCarcinogenicSRTByCuit: builder.mutation({
            query: ({ cuit, idInformes }: { cuit: string; idInformes: number[] }) => ({
                url: `${BASE_URL}/srt/cuit/${cuit}/vincular`,
                method: 'POST',
                body: { idInformes }
            }),
            onQueryStarted: async (_arg, { dispatch, queryFulfilled }) => {
                try {
                    await queryFulfilled;
                    dispatch(presentacionesApi.util.invalidateTags(['Presentaciones']));
                    dispatch(carcinogenicApi.util.invalidateTags(['CarcinogenicReportSRT']));
                } catch {
                    // On error, don't invalidate tags to avoid remounting the drawer and losing state.
                }
            }
        })
    })
});

export const {
    useCreateCarcinogenicReportMutation,
    useEditCarcinogenicReportEstablishmentMutation,
    useGetCarcinogenicParametricDataQuery,
    useGetCarcinogenicReportQuery,
    useGetSubstancesQuery,
    useEditCarcinogenicSubstanceMutation,
    useCreateCarcinogenicSubstanceStudyMutation,
    useDeleteCarcinogenicSubstanceStudyMutation,
    useCreateCarcinogenicProviderMutation,
    useEditCarcinogenicProviderMutation,
    useDeleteCarcinogenicProviderMutation,
    useCreateRadiologicalEquipmentMutation,
    useEditCarcinogenicEquipmentMutation,
    useDeleteCarcinogenicEquipmentMutation,
    useDeleteCarcinogenicEquipmentResponsibleMutation,
    useGetCarcinogenicReportWorkersQuery,
    useGetCarcinogenicReportWorkerDetailQuery,
    useLazyGetCarcinogenicReportWorkerDetailQuery,
    useEditCarcinogenicReportWorkerDetailMutation,
    useAddCarcinogenicReportContractorMutation,
    useEditCarcinogenicReportContractorMutation,
    useDeleteCarcinogenicContractorMutation,
    useAddCarcinogenicReportResponsibleMutation,
    useEditCarcinogenicReportResponsibleMutation,
    useDeleteCarcinogenicReportResponsibleMutation,
    useVincularCarcinogenicReportMutation,
    useSendCarcinogenicSRTMutation,
    useGetCarcinogenicDetailsHistoryQuery,
    useGetCarcinogenicSrtHistorialQuery,
    useGetCarcinogenicSrtValidacionesQuery,
    useLazyGetCarcinogenicSrtValidacionesQuery,
    useGetCarcinogenicSrtValidacionesLoteQuery,
    useLazyGetCarcinogenicSrtValidacionesLoteQuery,
    useCancelCarcinogenicSRTByContractMutation,
    useConsolidateCarcinogenicSRTMutation,
    useSyncCarcinogenicSRTNewsMutation,
    useModifyCarcinogenicSRTMutation,
    useLazyGetCarcinogenicPendingByCuitQuery,
    useGetCarcinogenicPendingByCuitQuery,
    useGetCuitPresentacionesSvccQuery,
    useVincularCarcinogenicSRTByCuitMutation,
    useLazyGetCarcinogenicSRTDebugByCuitQuery,
    useLazyVerificarSincronizacionSrtQuery,
    useGetValidacionesCuitsSvccQuery,
    useGetEstadoSvccPorInformeQuery,
    useGetCorreccionesPendientesEstablecimientoQuery,
    useNotificarClienteSvccMutation,
    useNotificarCorreccionCargadaMutation
} = carcinogenicApi;
